import {useEffect, useState} from 'react';
import {collection, limit, onSnapshot, query, where} from 'firebase/firestore';
import {getActiveCampaignRequiredFields} from '../services/firestoreService';
import {db, firestorePathId} from '../services/firebaseConfig';
import {verificarElegibilidadeRecadastramento} from './recadastramentoResumoCore';

const subscriptions = new Map();
const initialState = {
  campaignData: null,
  recadastramentoData: null,
  isVinculoElegivel: false,
  loading: true,
  error: null,
};

const notify = (entry) => entry.listeners.forEach((listener) => listener(entry.state));

const startSubscription = async (entry, {municipioId, cpf, vinculo}) => {
  try {
    const campaignData = await getActiveCampaignRequiredFields(municipioId);
    if (entry.disposed) return;
    const isVinculoElegivel = Boolean(
      campaignData?.id
      && verificarElegibilidadeRecadastramento(vinculo, campaignData.filters || {}),
    );
    entry.state = {
      ...initialState,
      campaignData,
      isVinculoElegivel,
      loading: Boolean(isVinculoElegivel),
    };
    notify(entry);
    if (!isVinculoElegivel) return;

    const path = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${campaignData.id}/servidores`;
    const statusQuery = query(
      collection(db, path),
      where('userCpf', '==', cpf),
      limit(1),
    );
    entry.unsubscribe = onSnapshot(statusQuery, (snapshot) => {
      const documentSnapshot = snapshot.docs[0];
      entry.state = {
        ...entry.state,
        recadastramentoData: documentSnapshot
          ? {id: documentSnapshot.id, ...documentSnapshot.data()}
          : null,
        loading: false,
        error: null,
      };
      notify(entry);
    }, (error) => {
      entry.state = {...entry.state, loading: false, error};
      notify(entry);
    });
  } catch (error) {
    if (entry.disposed) return;
    entry.state = {...initialState, loading: false, error};
    notify(entry);
  }
};

const subscribe = (key, params, listener) => {
  let entry = subscriptions.get(key);
  if (!entry) {
    entry = {
      state: initialState,
      listeners: new Set(),
      unsubscribe: null,
      disposed: false,
      cleanupTimer: null,
    };
    subscriptions.set(key, entry);
    void startSubscription(entry, params);
  }
  if (entry.cleanupTimer) {
    clearTimeout(entry.cleanupTimer);
    entry.cleanupTimer = null;
  }
  entry.listeners.add(listener);
  listener(entry.state);

  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size > 0) return;
    entry.cleanupTimer = setTimeout(() => {
      if (entry.listeners.size > 0) return;
      entry.disposed = true;
      entry.unsubscribe?.();
      subscriptions.delete(key);
    }, 0);
  };
};

const buildKey = ({municipioId, cpf, vinculo}) => [
  municipioId,
  cpf,
  vinculo?.vinculo_id ?? vinculo?.vinculo ?? vinculo?.matricula ?? '',
  vinculo?.admissao_tipo ?? '',
  vinculo?.situacao_folha ?? '',
].map((value) => String(value || '').trim()).join('|');

export const useRecadastramentoResumo = ({user, vinculo, disabled = false}) => {
  const municipioId = String(user?.municipioId || '').trim();
  const cpf = String(user?.cpf || '').replace(/\D/g, '');
  const key = !disabled && municipioId && cpf && vinculo
    ? buildKey({municipioId, cpf, vinculo})
    : '';
  const [state, setState] = useState(() => (
    key ? subscriptions.get(key)?.state || initialState : {...initialState, loading: false}
  ));

  useEffect(() => {
    if (!key) {
      setState({...initialState, loading: false});
      return undefined;
    }
    return subscribe(key, {municipioId, cpf, vinculo}, setState);
  // A chave inclui todos os campos do vínculo que alteram elegibilidade/escopo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
};

export const __resetRecadastramentoResumoForTests = () => {
  subscriptions.forEach((entry) => {
    if (entry.cleanupTimer) clearTimeout(entry.cleanupTimer);
    entry.unsubscribe?.();
  });
  subscriptions.clear();
};
